import cn from "classnames";
import { Button } from "components/Button";
import React, { forwardRef, useContext, useEffect, useRef, useState } from "react";
import { ErrorSummary } from "components/ErrorSummary";
import { submitByRefPromise } from "components/JsonForm/utils";
import Form from "@rjsf/core";
import { ListGroup, Offcanvas, Spinner, Stack } from "react-bootstrap";
import { ApprovedEquipmentDetailsModel, ApprovedEquipmentSearchFilter, EquipmentCatalog, EquipmentDetailsFromCatalog } from "./types";
import { ApprovedEquipmentSearchResult, useApprovedEquipmentSearch } from "./useApprovedEquipmentSearch";
import { useApprovedMeasureGridConfig } from "./useApprovedMeasureGridConfig";
import { createApprovedEquipment, createEquipment, getApprovedEquipmentRowId, getAttributeTitle, updateEquipment } from "./utils";
import { useApprovedEquipmentDetails } from "./useApprovedEquipmentDetails";
import { OffcanvasContext } from "./EquipmentAdd";
import { SubmitButton } from "components/Button/SubmitButton";
import { EquipmentBlockContext } from "components/utils/contexts";
import { Equipment } from "./Equipment";
import { EquipmentEditSubmitItem } from "types/EquipmentEditSubmitItem";
import { omit } from "lodash";
import { useEquipmentValidationTypes } from "components/utils/useEquipmentValidationTypes";
import { useTrueFalseTypes } from "components/utils/useTrueFalseTypes";
import { createId } from "components/utils/string";
import { EquipmentAddSubmitItem } from "types/EquipmentAddSubmitItem";
import { useToast } from "components/Toast";
import { EquipmentLabel } from "./EquipmentLabel";
import { ApprovedEquipmentGrid } from "./ApprovedEquipmentGrid";
import { DataGridPaging } from "components/DataGrid/DataGridPaging";
import useMediaQuery from "components/utils/useMediaQuery";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export const ApprovedEquipmentSearchResults = ({
    searchFilter,
    equipmentDetails,
    catalogNumber,
    onClose,
    onStartOver,
    activeCatalog,
    initialItemsFound,
    setInitialItemsFound,
}: ApprovedEquipmentSearchResultsProps) => {
    const { setIsWidePanel, setShowHeaderStartOver } = useContext(OffcanvasContext);
    const { applicationNumber, onEquipmentAddClose, onEquipmentAdded } = useContext(EquipmentBlockContext);
    const formRef = useRef<Form<any>>();
    const panelRef = useRef<HTMLDivElement>(null);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [showEquipmentDetails, setShowEquipmentDetails] = useState<boolean>(false);
    const [errorSummary, setErrorSummary] = useState<any>();
    const [liveValidate, setLiveValidate] = useState<boolean>(false);
    const [manualEntry, setManualEntry] = useState<boolean>(false);
    const toast = useToast();
    const isMobile = useMediaQuery("(max-width: 768px)");

    const [selectedEquipment, setSelectedEquipment] = useState<ApprovedEquipmentSearchResult>();

    const [pageSize, setPageSize] = useState(20);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [filter, setFilter] = useState<string>("");
    const [sortAsc, setSortAsc] = useState<boolean>(true);
    const [sortBy, setSortBy] = useState<string>("");
    const [showSearchGrid, setShowSearchGrid] = useState<boolean>(false);

    const [searchResults, isLoadingSearchResults, searchError] = useApprovedEquipmentSearch(
        applicationNumber,
        searchFilter,
        pageNumber,
        pageSize,
        filter,
        sortBy,
        sortAsc
    );

    const totalRecords = searchResults?.totalRecords ?? 0;
    const pagesCount = Math.ceil(totalRecords / pageSize);
    const equipmentList = searchResults?.items ?? [];

    const [gridConfig, isLoadingGridConfig] = useApprovedMeasureGridConfig(applicationNumber, searchFilter.industryMeasureNumber);
    const rowId = getApprovedEquipmentRowId(selectedEquipment, gridConfig);

    const [approvedEquipment, isLoadingApprovedEquipment, approvedEquipmentError] = useApprovedEquipmentDetails(
        searchFilter.industryMeasureNumber,
        rowId
    );

    const onSortChange = (key: string, forceAsc?: boolean) => {
        if (forceAsc !== undefined) {
            setSortBy(key);
            setSortAsc(forceAsc);
            return;
        }

        if (sortBy === key) {
            setSortAsc((prev) => !prev);
            return;
        }

        setSortBy(key);
        setSortAsc(true);
    };

    // Preload equipment details
    useEquipmentValidationTypes();
    useTrueFalseTypes();

    useEffect(() => {
        if (totalRecords === 1 && searchResults?.items?.length === 1) {
            setSelectedEquipment(searchResults.items[0]);
        }
    }, [searchResults?.items, totalRecords]);

    useEffect(() => {
        if ((approvedEquipment || initialItemsFound) && !showEquipmentDetails) {
            setIsWidePanel(true);
        }

        setShowHeaderStartOver(!showEquipmentDetails);

        return () => {
            setIsWidePanel(false);
            setShowHeaderStartOver(false);
        };
    }, [setIsWidePanel, setShowHeaderStartOver, approvedEquipment, initialItemsFound, showEquipmentDetails]);

    useEffect(() => {
        setErrorSummary(searchError ?? approvedEquipmentError);
    }, [searchError, approvedEquipmentError]);

    const resetPanelScroll = () => {
        if (panelRef.current) {
            panelRef.current.scrollTop = 0;
        }
    };

    const onSelect = (equipment: ApprovedEquipmentSearchResult) => {
        setSelectedEquipment(equipment);
        setShowSearchGrid(false);
        resetPanelScroll();
    };

    const onFilterChange = (newValue: string) => {
        if (newValue !== filter) {
            setFilter(newValue);
            setPageNumber(1);
        }
    };

    const onAcceptApprovedEquipment = () => {
        setShowEquipmentDetails(true);
        setIsWidePanel(false);
        resetPanelScroll();
    };

    const onAdd = async () => {
        try {
            setIsSubmitting(true);
            setLiveValidate(true);
            const formData = await submitByRefPromise(formRef);

            if (applicationNumber && rowId) {
                const quantity = formData.quantity;
                const response = await createApprovedEquipment(applicationNumber, searchFilter.industryMeasureNumber, rowId, quantity);

                if (response) {
                    const equipmentNumber = response.equipid;
                    const equipmentItem: EquipmentEditSubmitItem = {
                        catalogNumber,
                        quantity,
                        attributes: Object.keys(omit(formData, "quantity")).map((key: string) => ({
                            attributename: key,
                            attributevalue: String(formData[key] ?? ""),
                        })),
                    };

                    await updateEquipment(applicationNumber, equipmentNumber, equipmentItem);
                    toast.success(response.responseMessage);
                }

                onEquipmentAdded && onEquipmentAdded();
                onEquipmentAddClose && onEquipmentAddClose();
            }
        } catch (error) {
            setErrorSummary(error);
            setIsSubmitting(false);
            setTimeout(() => {
                setLiveValidate(false);
            });
        }
    };

    const onBack = () => {
        setShowSearchGrid(true);
        resetPanelScroll();
    };

    const onManualEntryAdd = async () => {
        try {
            const formData = await submitByRefPromise(formRef);

            if (applicationNumber && catalogNumber) {
                const equipmentItem: EquipmentAddSubmitItem = {
                    equipID: createId(),
                    catalogNumber,
                    quantity: formData.quantity,
                    attributes: Object.keys(omit(formData, "quantity")).map((key: string) => ({
                        attributename: key,
                        attributevalue: String(formData[key] ?? ""),
                    })),
                };

                setIsSubmitting(true);
                const response = await createEquipment(applicationNumber, equipmentItem);
                toast.success(response.responseMessage);
            }

            onEquipmentAdded && onEquipmentAdded();
            onEquipmentAddClose && onEquipmentAddClose();
            setManualEntry(false);
        } catch (error) {
            setErrorSummary(error);
            setIsSubmitting(false);
        }
    };

    const onManualEntry = () => {
        setManualEntry(true);
        setShowEquipmentDetails(true);
        resetPanelScroll();
    };

    if (isLoadingGridConfig && (isLoadingSearchResults || isLoadingApprovedEquipment)) {
        return (
            <Panel ref={panelRef}>
                <Spinner className="align-self-center flex-shrink-0" animation="border" role="status">
                    <span className="visually-hidden">Searching approved equipment...</span>
                </Spinner>
                <PanelControls onBack={onStartOver} backButtonText="Start Over" />
            </Panel>
        );
    }

    if (showEquipmentDetails) {
        // fill attribute values with data from approved equipment search
        const equipmentDetailsFromCatalog: EquipmentDetailsFromCatalog = {
            ...equipmentDetails,
            attributes: equipmentDetails.attributes.map((attribute) => ({
                ...attribute,
                value:
                    approvedEquipment?.attributes?.find(
                        (approvedEquipmentAttribute) =>
                            (approvedEquipmentAttribute.attributeName ?? approvedEquipmentAttribute.attribute ?? "").toLowerCase() ===
                            (attribute.attributeName ?? "").toLowerCase()
                    )?.val ??
                    attribute.value ??
                    attribute.defaultValue,
            })),
        };

        return (
            <>
                <Panel ref={panelRef}>
                    {activeCatalog && (
                        <>
                            <EquipmentLabel equipment={activeCatalog} />
                            <hr />
                        </>
                    )}
                    <ErrorSummary errorSummary={errorSummary} />
                    <Equipment formRef={formRef} item={equipmentDetailsFromCatalog} liveValidate={liveValidate} />
                </Panel>
                <PanelControls
                    className="justify-content-between border-top p-3"
                    onBack={onStartOver}
                    backButtonText="Start Over"
                    isSubmitting={isSubmitting}
                    onSubmit={manualEntry ? onManualEntryAdd : onAdd}
                />
            </>
        );
    }

    if (approvedEquipment && !showSearchGrid) {
        return (
            <Panel ref={panelRef} className="px-2 pb-2">
                <ErrorSummary errorSummary={errorSummary} />
                <p>We've located the equipment that we think matches the item you are attempting to add.</p>
                <p>
                    Please review the information displayed below to verify that it is the correct item you would like to add. If the
                    information is correct, press the button below to continue.
                </p>
                <ApprovedEquipmentDetails approvedEquipment={approvedEquipment} />
                <PanelControls
                    isSubmitting={isSubmitting}
                    submitButtonText="Continue"
                    onBack={onBack}
                    onSubmit={onAcceptApprovedEquipment}
                />
            </Panel>
        );
    }

    if (!initialItemsFound && totalRecords === 0) {
        return (
            <Panel ref={panelRef}>
                <ErrorSummary errorSummary={errorSummary} />
                <p>
                    We could not locate any equipment that matches the item you are attempting to add. Click <b>Start Over</b> to try
                    searching again.
                </p>
                <p>
                    To manually enter the equipment information instead, click <b>Manual Entry</b>.
                </p>
                <PanelControls onBack={onStartOver} backButtonText="Start Over" onManualEntry={onManualEntry} />
            </Panel>
        );
    }

    if (initialItemsFound || (totalRecords > 1 && equipmentList.length > 1) || showSearchGrid) {
        return (
            <Panel ref={panelRef}>
                <ErrorSummary errorSummary={errorSummary} />
                {!isMobile && (
                    <p style={{ color: "#706B85" }}>
                        <FontAwesomeIcon icon={["fal", "circle-info"]} className="me-2" />
                        Use * as a wildcard in your search to find potential matches (e.g., ABC123*).
                    </p>
                )}
                <ApprovedEquipmentGrid
                    items={equipmentList}
                    gridConfig={gridConfig}
                    onSelect={onSelect}
                    sortBy={sortBy}
                    sortAsc={sortAsc}
                    totalRecords={totalRecords}
                    onSortChange={onSortChange}
                    onFilterChange={onFilterChange}
                    filter={filter}
                    isLoadingGrid={isLoadingSearchResults}
                    setInitialItemsFound={setInitialItemsFound}
                />
                <div
                    className={cn({ "position-sticky sticky-bottom p-2 border-top": isMobile, "py-3": !isMobile })}
                    style={{ backgroundColor: "var(--table-background-color)" }}
                >
                    <DataGridPaging
                        pageNumber={pageNumber}
                        pagesCount={pagesCount}
                        pageSize={pageSize}
                        totalRecords={totalRecords}
                        onPageChange={setPageNumber}
                        onPageSizeChange={setPageSize}
                    />
                    {isMobile && (
                        <Button
                            className="align-self-start d-flex gap-2 align-items-center position-sticky sticky-bottom"
                            variant="secondary"
                            onClick={onStartOver}
                        >
                            <FontAwesomeIcon icon="arrow-left" />
                            Start over
                        </Button>
                    )}
                </div>
            </Panel>
        );
    }

    return null;
};

interface ApprovedEquipmentSearchResultsProps {
    searchFilter: ApprovedEquipmentSearchFilter;
    equipmentDetails: EquipmentDetailsFromCatalog;
    catalogNumber: string;
    onClose: () => void;
    onStartOver: () => void;
    activeCatalog?: EquipmentCatalog;
    initialItemsFound: boolean;
    setInitialItemsFound: React.Dispatch<React.SetStateAction<boolean>>;
}

const Panel = forwardRef<HTMLDivElement, { children: React.ReactNode; className?: string }>(({ children, className }, ref) => {
    return (
        <Offcanvas.Body ref={ref} className={cn("d-flex flex-column p-0", className)}>
            {children}
        </Offcanvas.Body>
    );
});

const PanelControls = ({
    className,
    isSubmitting,
    submitButtonText = "Submit",
    backButtonText = "Back",
    onSubmit,
    onBack,
    onManualEntry,
    manualEntryButtonText = "Manual Entry",
}: PanelControlsProps) => {
    const isMobile = useMediaQuery("(max-width: 768px)");

    return (
        <Stack className={cn("equipment-controls flex-shrink-0 mt-auto", className)} direction="horizontal" gap={isMobile ? 1 : 3}>
            {onBack && (
                <Button variant="secondary" onClick={onBack} disabled={isSubmitting}>
                    {backButtonText}
                </Button>
            )}
            {onSubmit && (
                <SubmitButton isSubmitting={isSubmitting} spinnerText="Adding item to application..." onClick={onSubmit}>
                    {submitButtonText}
                </SubmitButton>
            )}
            {onManualEntry && (
                <SubmitButton className="ms-auto" onClick={onManualEntry} spinnerText="Adding item to application...">
                    {manualEntryButtonText}
                </SubmitButton>
            )}
        </Stack>
    );
};

interface PanelControlsProps {
    className?: string;
    isSubmitting?: boolean;
    submitButtonText?: string;
    backButtonText?: string;
    manualEntryButtonText?: string;
    onSubmit?: () => void;
    onBack?: () => void;
    onManualEntry?: () => void;
}

const ApprovedEquipmentDetails = ({ approvedEquipment }: ApprovedEquipmentDetailsProps) => {
    return (
        <ListGroup className="approved-equipment-details-list mb-2 flex-shrink-0">
            {approvedEquipment.attributes.map((item, index) => {
                const id = `${approvedEquipment.industryMeasureNumber}-${approvedEquipment.rowId}-${index}`;
                return (
                    <ListGroup.Item key={index} className="d-flex justify-content-between align-items-start">
                        <div id={id} className="fw-bold me-auto text-break">
                            {getAttributeTitle(undefined, item.attribute)}
                        </div>
                        <div className="attribute-value text-break" aria-labelledby={id}>
                            {item.val}
                        </div>
                    </ListGroup.Item>
                );
            })}
        </ListGroup>
    );
};

interface ApprovedEquipmentDetailsProps {
    approvedEquipment: ApprovedEquipmentDetailsModel;
}
